import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import Image from 'components/Common/Image'
import NestableClickable from 'components/Common/NestableClickable'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely'
import TourV2UrgencyTags from 'components/Tours/TourV2UrgencyTags'
import NumberRating from 'components/Common/NumberRating'
import TourTilePricing from 'components/Tours/TourTiles/TourTilePricing'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/v2/OfferListEventsContext'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import zIndex from 'styles/tools/z-index'
import UrgencyLabels from 'components/Luxkit/Label/UrgencyLabel'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'

const TourImageContainer = styled.div`
  position: relative;
  height: ${rem(200)};
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.seven};
`

const BadgeContainer = styled.div`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(2)};
`

const BookmarkContainer = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  top: ${rem(8)};
  right: ${rem(8)};
  z-index: ${zIndex.floatingBookmarkButton};
  width: fit-content;
  margin-left: auto;
`

const Tile = styled(NestableClickable)`
  background-color: ${props => props.theme.palette.neutral.eight};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: ${rem(320)};
  height: 100%;

  ${mediaQueryUp.tablet} {
    min-height: ${rem(364)};
  }
`

const TileContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: ${rem(20)};

  > * + * {
    margin-top: ${rem(8)};
  }
`

const DetailsCaptionContainer = styled(BodyText)`
  display: flex;

  > * + *::before {
    color: ${({ theme }) => theme.palette.neutral.one};
    content: '·';
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
`

interface Props {
  tour: Tours.V2Offer;
  to?: string;
  className?: string;
  isVelocityEnabled?: boolean;
  category?: string | undefined;
  index?: number;
}

function TourTile(props: Props) {
  const {
    className,
    tour,
    to,
    isVelocityEnabled,
    category,
    index,
  } = props

  const { cheapestVariation, cheapestPurchasableOption } = useCheapestOfTourV2Offer(tour)
  const {
    hasDiscount,
  } = useTourV2PurchasableOptionDetails(cheapestPurchasableOption)

  const {
    startLocation,
    endLocation,
    hasTheSameStartAndEndLocation,
    placesCount,
    countriesCount,
    durationInDaysCount,
  } = useTourV2VariationDetails(cheapestVariation)

  const isTourReviewsABTestEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.isTourReviewsEnabled)
  const isTourReviewsVisible = isTourReviewsABTestEnabled && tour.reviewsTotal > 0 && tour.reviewsRating > 0

  const onEvent = useContext(OfferListEventsContext)
  const onClickView = useCallback(() => {
    if (onEvent) {
      const params = {
        category,
        index,
        tour,
        key: EventDataKey.ProductClick,
      }
      onEvent(OfferListEvents.productClick, params)
    }
  }, [category, onEvent, tour, index])

  return (
    <Tile
      to={to}
      as={ClickableLink}
      className={className}
      onClick={onClickView}
    >
      <TourImageContainer>
        {cheapestVariation?.images && <Image
          height={200}
          image={cheapestVariation?.images[0]}
          fit="center"
          dpr={2}
        />}
        {isVelocityEnabled && (
          <BadgeContainer>
            <PartnershipLabel canEarn canUse variant="icon"/>
          </BadgeContainer>
        )}
        <TripGuard>
          <BookmarkContainer>
            <BookmarkButton offer={tour} />
          </BookmarkContainer>
        </TripGuard>
      </TourImageContainer>
      <TileContent>
        <div>
          <VerticalSpacer gap={8}>
            <VerticalSpacer gap={4}>
              {hasDiscount && <UrgencyLabels variant="critical2">Sale</UrgencyLabels>}
              <TourV2UrgencyTags tags={tour.urgencyTags} />
              <Caption variant="medium" wrap="truncate">
                {hasTheSameStartAndEndLocation && <span>Starts and ends in {endLocation}</span>}
                {!hasTheSameStartAndEndLocation && <span>Starts in {startLocation}, Ends in {endLocation}</span>}
              </Caption>
              <BodyText lineClamp={2} variant="large" weight="bold">{tour.name}</BodyText>
            </VerticalSpacer>
            <BodyText as="span" variant="small" weight="bold">{tour.brand.name}</BodyText>
            {isTourReviewsVisible && (
              <NumberRating
                variant="xs"
                rating={tour.reviewsRating}
                total={tour.reviewsTotal}
                ratingSubtitle={tour.reviewsSource === 'le_tour_operator' ? 'Tour Operator rating' : undefined}
              />
            )}
            <Group direction="vertical" gap={8}>
              <DetailsCaptionContainer variant="small" colour="neutral-one">
                <div>{durationInDaysCount}</div>
                {placesCount && <div>{placesCount}</div>}
                {countriesCount && <div>{countriesCount}</div>}
              </DetailsCaptionContainer>
            </Group>
          </VerticalSpacer>
        </div>
        <TourTilePricing tour={tour} />
      </TileContent>
    </Tile>
  )
}

export default TourTile
